// ═══════════════════════════════════════════════════════════════════════════
// SponsorsTab — admin CRUD for the sponsors catalog (migration 072). A form
// (add / edit) over a list of sponsors. Writes go through has_cms_access-gated
// RPCs and are audit-logged. Mounts inside AdminPage's tab strip.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { listSponsorsAdmin, saveSponsor, removeSponsor } from '../../lib/sponsorsAdmin';

const BLANK = {
  id: null, name: '', category: '', logo: '', tagline: '', budget: '',
  min_eps: 0, niches: '', status: 'open', is_active: true, position: 0,
};

export default function SponsorsTab() {
  const toast = useToast();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setSponsors(await listSponsorsAdmin()); }
    catch (e) { toast.error(e?.message || 'Could not load sponsors.'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { refresh(); }, [refresh]);

  function startAdd() { setForm(BLANK); setShowForm(true); }
  function cancel()   { setForm(BLANK); setShowForm(false); }

  function edit(s) {
    setForm({
      id: s.id, name: s.name || '', category: s.category || '', logo: s.logo || '',
      tagline: s.tagline || '', budget: s.budget || '', min_eps: s.min_eps ?? 0,
      niches: (s.niches || []).join(', '), status: s.status || 'open',
      is_active: s.is_active !== false, position: s.position ?? 0,
    });
    setShowForm(true);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  }

  async function save() {
    if (!form.name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      await saveSponsor(form.id, {
        name: form.name.trim(),
        category: form.category.trim() || null,
        logo: form.logo.trim() || null,
        tagline: form.tagline.trim() || null,
        budget: form.budget.trim() || null,
        min_eps: parseInt(form.min_eps, 10) || 0,
        niches: form.niches.split(',').map(n => n.trim()).filter(Boolean),
        status: form.status,
        is_active: !!form.is_active,
        position: parseInt(form.position, 10) || 0,
      });
      toast.success(form.id ? 'Sponsor updated.' : 'Sponsor added.');
      cancel();
      await refresh();
    } catch (e) { toast.error(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  }

  async function remove(s) {
    if (!window.confirm(`Remove ${s.name}? This can’t be undone.`)) return;
    setSaving(true);
    try { await removeSponsor(s.id); if (form.id === s.id) cancel(); await refresh(); }
    catch (e) { toast.error(e?.message || 'Could not remove.'); }
    finally { setSaving(false); }
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div className="spo">
      <div className="spo-header">
        <div>
          <h2 className="mkt-panel-title">Sponsors</h2>
          <p className="mkt-panel-sub">
            The curated catalog behind the host Earn surface + the “Your next step” unlock.
            {sponsors.length > 0 && <> {sponsors.length} total.</>}
          </p>
        </div>
        {!showForm && (
          <button type="button" className="spo-add" onClick={startAdd}>+ New sponsor</button>
        )}
      </div>

      {/* ── Add / edit form (only when adding or editing) ── */}
      {showForm && (
        <div className="spo-form">
          <div className="spo-form-title">{form.id ? `Edit ${form.name || 'sponsor'}` : 'New sponsor'}</div>
          <div className="spo-grid">
            <label className="spo-field spo-field--wide"><span>Name</span><input autoFocus value={form.name} onChange={set('name')} placeholder="Riverside.fm" /></label>
            <label className="spo-field"><span>Category</span><input value={form.category} onChange={set('category')} placeholder="Podcast Tools" /></label>
            <label className="spo-field"><span>Status</span>
              <select value={form.status} onChange={set('status')}>
                <option value="open">Open — accepting now</option>
                <option value="soon">Soon — coming up</option>
                <option value="closed">Closed — not active</option>
              </select>
            </label>
            <label className="spo-field spo-field--wide"><span>Tagline</span><input value={form.tagline} onChange={set('tagline')} placeholder="Record studio-quality audio & video remotely." /></label>
            <label className="spo-field"><span>Budget</span><input value={form.budget} onChange={set('budget')} placeholder="$500–$2,000/ep" /></label>
            <label className="spo-field spo-field--sm"><span>Min episodes to qualify</span><input type="number" min="0" value={form.min_eps} onChange={set('min_eps')} /></label>
            <label className="spo-field spo-field--wide"><span>Niches (comma-separated)</span><input value={form.niches} onChange={set('niches')} placeholder="Technology, Business, Creativity" /></label>
            <label className="spo-field spo-field--sm"><span>Logo (emoji)</span><input value={form.logo} onChange={set('logo')} placeholder="🎙" maxLength={4} /></label>
            <label className="spo-field spo-field--sm"><span>Sort position</span><input type="number" value={form.position} onChange={set('position')} /></label>
            <label className="spo-check"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> <span>Show on the Earn surface</span></label>
          </div>
          <div className="spo-form-actions">
            <button type="button" className="feat-btn feat-btn--primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add sponsor'}
            </button>
            <button type="button" className="feat-btn" disabled={saving} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── List ── */}
      {loading ? (
        <div className="mkt-empty">Loading…</div>
      ) : sponsors.length === 0 ? (
        <div className="mkt-empty">No sponsors yet. Add your first one to power the host Earn surface.</div>
      ) : (
        <div className="feat-list">
          {sponsors.map((s) => (
            <div key={s.id} className={`feat-row${s.is_active ? '' : ' feat-row--inactive'}`}>
              <div className="spo-logo" aria-hidden>{s.logo || '💼'}</div>
              <div className="feat-main">
                <div className="feat-title">
                  {s.name}
                  <span className={`spo-status spo-status--${s.status}`}>{s.status}</span>
                  {!s.is_active && <span className="spo-status spo-status--hidden">hidden</span>}
                </div>
                <div className="feat-creator">{s.budget || 'No budget set'} · min {s.min_eps} ep · {(s.niches || []).join(', ') || 'any niche'}</div>
              </div>
              <div className="feat-actions">
                <button type="button" className="feat-btn" disabled={saving} onClick={() => edit(s)}>Edit</button>
                <button type="button" className="feat-btn feat-btn--danger" disabled={saving} onClick={() => remove(s)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
